import React from "react";
import Link from "@material-ui/core/Link/Link";
import Typography from "@material-ui/core/Typography/Typography";

export interface IFAQItem {
    q: string;
    a: JSX.Element;
}
export interface IFAQSection {
    name: string;
    content: IFAQItem[];
}

export const FAQs: IFAQSection[] = [
    {
        name: "General information",
        content: [
            {
                q: "What is the Deutsche Börse Data Shop?",
                a: (
                    <>
                        <p>
                            The Deutsche Börse Data Shop is a self-service platform which offers you easy access
                            to detailed and accurate historical market data, analytics and 360T FX data, allowing
                            you to perform valuable analysis to optimise your  trading strategies. Our Data Shop
                            lets you pick and choose trading venues, time periods and instruments, thereby tailoring
                            the information exactly to your needs – 24 hours a day.
                        </p>
                        <p>
                            To ensure smart, flexible, and efficient delivery of all data, we have set up a
                            high-performance, cloud-based infrastructure that scales from one to thousands of securities
                            in seconds. A state-of-the-art user interface covering all types of front-end devices allows
                            you to quickly and easily integrate data into your business processes.
                        </p>
                    </>
                )
            },
            {
                q: "What sort of information does the Data Shop provide?",
                a: (
                    <>
                        <p>
                            Our Data Shop offers historical  data from our trading venues Eurex, Xetra and 360T, a 
                            variety of analytics as well as reference data. Our offering includes data capturing 
                            every transaction price in the market, complete historical order book and trading data
                            for Eurex and Xetra, as well as reference data.
                        </p>
                        <p>
                            Our broad analytics portfolio provides you with deep insights into liquidity, market
                            concentration and market trends.
                        </p>
                        <p>
                            Please find more detailed information in the <Link href="/products">product overview</Link>.
                        </p>
                    </>
                )
            },
            {
                q: "What history does the Data Shop offer?",
                a: (
                    <>
                        <p>
                            The history for market data stream files depends on the respective product selected and can
                            go back up as far as 2014.
                        </p>
                        <Typography variant="h5">Analytics:</Typography>
                        <p>
                            The analytics which are derived from Eurex and Xetra trading data are available on a t+1
                            basis or respectively t+2 for Eurex Open Interest Insights.
                        </p>
                        <p>
                            The history for Eurex Open Interest Insights can be retrieved back to 2002 with previous
                            stringent liquidity rules removed for expiries 2002-2018.
                        </p>
                        <p>The history for Xetra Flow Insights is available back to July 2017.</p>
                        <p>
                            The history for Xetra HHI Insights goes back to 1 July 2017, for Eurex HHI Insights back to
                            1 January 2017.
                        </p>
                    </>
                )
            },
            {
                q: "Which types of data purchase does the Data Shop offer?",
                a: (
                    <p>
                        You can subscribe to daily data as well as retrieve one-off histories on an ad hoc basis
                        (so-called one-off orders). The pricing model ensures that you only pay for the data you need
                        and receive transparent price information. You can pay by both credit card or bank transfer.
                    </p>
                )
            },
            {
                q: "Who can order data in the Data Shop?",
                a: (
                    <>
                        Contracts for the sale and purchase of Historical Data will only be concluded on a
                        "Business-to-Business" level, i.e.
                        <ul>
                            <li>With enterprise operators exercising their trade, business or profession </li>
                            <li>With a legal person under public law </li>
                            <li>
                                With a special fund under public law within the meaning of sec. 310 para. 1 of the
                                German Civil Code (any and all of these being an “Enterprise”).
                            </li>
                        </ul>
                        <p>
                            A contract is not concluded on a "Business-to-Customer" level. Please refer to our&nbsp;
                            <Link href="/terms-and-conditions">General Terms and Conditions</Link>
                            &nbsp;for further information.
                        </p>
                    </>
                )
            },
            {
                q: "Where can I get support?",
                a: (
                    <p>
                        If you need further support, please contact our Customer Service team at +49-(0) 69-2 11-11 0 33
                        [8.00-18.00 CET] or via email at{" "}
                        <a href="mailto:hdp@deutsche-boerse.com.">hdp@deutsche-boerse.com.</a>
                    </p>
                )
            }
        ]
    },
    {
        name: "Order + Data",
        content: [
            {
                q: "How do I order data from Data Shop?",
                a: (
                    <p>
                        By clicking on <Link href="/products">Product overview</Link> you can filter by product category
                        and/or trading venue and search for the desired product. Once the product is defined, please
                        select the date range and the order item can be placed in the shopping cart. Once you have added
                        the item to your shopping cart, you can either add more items or go to the shopping cart and
                        check out. During the check-out process, you can review the content of your basket, verify the
                        address and personal details, select your preferred payment method and finally confirm the
                        order.
                        <br /> For orders totaling less than 100 Euro, we charge an administrative fee of 30 Euro per
                        order.
                    </p>
                )
            },
            {
                q: "When is my data available for download?",
                a: (
                    <p>
                        In the case of a one-off order paid by credit card, the data is immediately available in your
                        account under "My orders". In the case of a one-off order paid by bank transfer, the data will
                        be available after receipt of payment. In any case, you will be informed by e-mail that the data
                        package has been made available and receive a link to download the data package.
                    </p>
                )
            },
            {
                q: "What file format is provided?",
                a: <p>The data can be downloaded as CSV and/or pcap file.</p>
            },
            {
                q: "Where can I find my download link?",
                a: <p>Your download link is provided in your personal account under 'My Orders'.</p>
            },
            {
                q: "How do I download files in the Data Shop?",
                a: <p>We provide you with a user manual in your account under 'Documentation'.</p>
            },
            {
                q: "How long are the files available after the order confirmation?",
                a: (
                    <p>
                        A customer is entitled for a one-time download. The file is available for download for 90 days
                        from the date of order confirmation. Please note our{" "}
                        <Link href="/terms-and-conditions">General Terms and Conditions</Link>.
                    </p>
                )
            },
            {
                q: "How do I request a subscription?",
                a: (
                    <p>
                        If you would like to receive your data regularly, you can request a subscription for the
                        respective product under 'Product Overview' in the Data Shop. You can use the standard form to
                        submit the order. As soon as we have received the information, we will contact you.
                    </p>
                )
            }
        ]
    },
    {
        name: "Account",
        content: [
            {
                q: "How can I reset my password?",
                a: (
                    <p>
                        If you have forgotten your password, you will be asked to enter your username on the Login page
                        and we will send you a message to reset your password.
                        <br />
                        Furthermore, you password has to be changed every 90 days.
                    </p>
                )
            },
            {
                q: "How do I create an user account?",
                a: (
                    <p>
                        If you have never registered with Data Shop, you can create an account on the{" "}
                        <Link href="/register">Register page</Link>.
                    </p>
                )
            },
            {
                q: "Where can I change my personal information?",
                a: (
                    <p>
                        By clicking <Link href="/my-account">My account</Link>, you can change your billing data as well
                        as your name or address under "Personal data".
                    </p>
                )
            },
            {
                q: "How can I login?",
                a: <p>Please enter your user name and password on the login page to access your Data Shop account.</p>
            }
        ]
    },
    {
        name: "Documentation",
        content: [
            {
                q: "Where can I find T7 release documents, parser and API?",
                a: <p>All T7 Release Documents, Parser and API are provided in your account under "Documentation".</p>
            }
        ]
    }
];
